import React, { Fragment, useEffect, useState } from 'react';
import {
    Edit,
    Show,
    TabbedShowLayout,
    useDataProvider,
    useNotify,
    useRecordContext,
    useTranslate,
    TextField,
    SelectField,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    FormControl,
    FormLabel,
    MenuItem,
    Select,
    Typography,
    Grid,
    Divider,
    IconButton,
    Snackbar,
    Button,
    Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams } from 'react-router';
import { checkResponse } from '../dataProvider';
import { Flow, FlowView, layoutFlow } from './Flow';
import { DataSetForm, Mode } from './DataSetForm';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

const StyledBox = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
}));

const LabelTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    minWidth: '80px',
    fontWeight: 500,
}));

const ValueTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

// Container: Handles boundaries, scrolling, and background
const CodeBlockContainer = styled(Box)(({ theme }) => ({
    position: 'relative', // Anchor for the absolute positioned button
    width: '100%', // Take full available width from parent
    maxWidth: '100%', // Prevent exceeding parent width
    backgroundColor: '#2d2d2d', // Match 'tomorrow' theme background
    borderRadius: '4px', // Apply rounding to the container
    marginTop: theme.spacing(1), // Add some space above the block
    marginBottom: theme.spacing(1), // Add some space below the block
    '& pre': {
        whiteSpace: 'pre-wrap', // Allow wrapping controlled by the component props
        margin: 0,
        padding: '16px',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'auto', // Add overflow auto to handle content that might still exceed width
    },
    overflow: 'hidden', // Prevent container from showing scrollbars
}));

// Button: Positioned absolutely relative to the container
const CopyButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute', // Position relative to the CodeBlockContainer
    top: '12px', // Adjust vertical position (considering padding)
    right: '12px', // Adjust horizontal position (considering padding)
    zIndex: 2, // Ensure it's above the code and scrollbar potentially
    color: theme.palette.grey[400], // Visible against dark background
    backgroundColor: 'rgba(255, 255, 255, 0.15)', // Slightly more opaque
    padding: '4px', // Smaller button padding
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        color: theme.palette.grey[200],
    },
}));

const DataSetShowLayout = () => {
    const translate = useTranslate();
    const record = useRecordContext();

    const dataProvider = useDataProvider();
    const { id } = useParams();
    const notify = useNotify();

    console.log(`DataSetShow record: ${JSON.stringify(record)}`);

    const copyToClipboard = async (text: string) => {
        if (!text) return false;

        try {
            if (window.isSecureContext && navigator.clipboard) {
                await navigator.clipboard.writeText(text);
                return true;
            }

            // Fallback for non-secure context
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                textArea.remove();
                return true;
            } catch (err) {
                textArea.remove();
                return false;
            }
        } catch (err) {
            return false;
        }
    };

    const pulsarTopic =
        record?.spec?.catalog === 'pulsar'
            ? `persistent://t-${record?.metadata?.tenant}/default/${record?.metadata?.name}`
            : undefined;

    const handleCopy = async (text: string) => {
        const success = await copyToClipboard(text);
        if (success) {
            notify('resources.datasets.show.copiedToClipboard', {
                type: 'success',
                autoHideDuration: 2000,
            });
        } else {
            notify('resources.datasets.show.copyFailed', {
                type: 'error',
                autoHideDuration: 2000,
            });
        }
    };

    // Define producerCode and consumerCode constants directly with the full token
    const producerCode = `import pulsar
import json
from datetime import datetime

service_url = "pulsar+ssl://pulsar.cloud.unitestream.com:6651"
token = "${record?.status?.pulsar?.token || ''}"
topic = "${pulsarTopic}"

client = pulsar.Client(service_url, authentication=pulsar.AuthenticationToken(token))

producer = client.create_producer(topic)

data = {
    "timestamp": datetime.now().isoformat(),
    "value": "Hello, Pulsar!"
}

try:
    message = json.dumps(data)
    producer.send(message.encode('utf-8'))
    print(f"Message sent: {message}")
except Exception as e:
    print(f"Error sending message: {e}")
finally:
    producer.close()
    client.close()`;

    const consumerCode = `import pulsar

service_url = "pulsar+ssl://pulsar.cloud.unitestream.com:6651"
token = "${record?.status?.pulsar?.token || ''}"
topic = "${pulsarTopic}"

client = pulsar.Client(service_url, authentication=pulsar.AuthenticationToken(token))
consumer = client.subscribe(
    topic,
    "my-subscription",
    initial_position=pulsar.InitialPosition.Latest
)

while True:
    try:
        msg = consumer.receive()
        print("Received message '{0}' id='{1}'".format(msg.data().decode('utf-8'), msg.message_id()))
        consumer.acknowledge(msg)
    except pulsar.Interrupted:
        print("Stop receiving messages")
        break

consumer.close()
client.close()`;

    return (
        <Box width="100%">
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                        {translate('resources.datasets.show.basicInfo')}
                    </Typography>

                    <StyledBox>
                        <LabelTypography>
                            {translate('resources.datasets.form.name')}
                        </LabelTypography>
                        <ValueTypography>
                            {record?.metadata?.name || id}
                            <IconButton
                                size="small"
                                onClick={() =>
                                    handleCopy(
                                        record?.metadata?.name || id || ''
                                    )
                                }
                            >
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </ValueTypography>
                    </StyledBox>

                    <StyledBox>
                        <LabelTypography>
                            {translate('resources.datasets.form.type')}
                        </LabelTypography>
                        <ValueTypography>
                            {record?.spec?.type || '-'}
                        </ValueTypography>
                    </StyledBox>

                    <StyledBox>
                        <LabelTypography>
                            {translate('resources.datasets.form.catalog')}
                        </LabelTypography>
                        <ValueTypography>
                            {record?.spec?.catalog || '-'}
                        </ValueTypography>
                    </StyledBox>

                    <StyledBox>
                        <LabelTypography>
                            {translate('resources.datasets.form.creationTime')}
                        </LabelTypography>
                        <ValueTypography>
                            {record?.metadata?.creationTimestamp
                                ? new Date(
                                      record.metadata.creationTimestamp
                                  ).toLocaleString('zh-CN')
                                : '-'}
                        </ValueTypography>
                    </StyledBox>

                    {pulsarTopic && (
                        <StyledBox>
                            <LabelTypography>
                                {translate(
                                    'resources.datasets.show.pulsarTopic'
                                )}
                            </LabelTypography>
                            <ValueTypography>
                                {pulsarTopic}
                                <IconButton
                                    size="small"
                                    onClick={() => handleCopy(pulsarTopic)}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </ValueTypography>
                        </StyledBox>
                    )}
                </CardContent>
            </Card>

            <Card sx={{ mt: 2, p: 2 }}>
                <CardContent>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            {translate('resources.datasets.show.pythonExample')}
                        </Typography>
                    </Box>

                    <Paper
                        elevation={0}
                        sx={{
                            mb: 3,
                            p: 2,
                            bgcolor: '#f5f5f5',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Typography
                            variant="h6"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                        >
                            {translate('resources.datasets.show.pythonSetup')}
                        </Typography>
                        <Typography variant="body1" paragraph>
                            {translate(
                                'resources.datasets.show.pythonSetupDescription'
                            )}
                        </Typography>
                        <Typography variant="body1" paragraph>
                            {translate(
                                'resources.datasets.show.pythonInstallStep1'
                            )}{' '}
                            <a
                                href="https://www.python.org/downloads/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                python.org
                            </a>
                        </Typography>
                        <Typography variant="body1" paragraph>
                            {translate(
                                'resources.datasets.show.pythonInstallDescription'
                            )}
                        </Typography>
                        <CodeBlockContainer>
                            <CopyButton
                                size="small"
                                onClick={() =>
                                    handleCopy('pip install pulsar-client')
                                }
                            >
                                <ContentCopyIcon fontSize="small" />
                            </CopyButton>
                            <SyntaxHighlighter
                                language="bash"
                                style={tomorrow}
                                customStyle={{
                                    margin: 0,
                                    padding: '16px',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    backgroundColor: 'transparent',
                                    overflow: 'auto',
                                    wordBreak: 'break-word',
                                }}
                                showLineNumbers
                                wrapLines={true}
                                lineProps={{
                                    style: {
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    },
                                }}
                            >
                                {`pip install pulsar-client`}
                            </SyntaxHighlighter>
                        </CodeBlockContainer>
                    </Paper>

                    <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                    >
                        {translate('resources.datasets.show.exampleCode')}
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="h6"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                        >
                            {translate(
                                'resources.datasets.show.producerExample'
                            )}
                        </Typography>
                        <CodeBlockContainer>
                            <CopyButton
                                size="small"
                                onClick={() => handleCopy(producerCode)}
                            >
                                <ContentCopyIcon fontSize="small" />
                            </CopyButton>
                            <SyntaxHighlighter
                                language="python"
                                style={tomorrow}
                                customStyle={{
                                    margin: 0,
                                    padding: '16px',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    backgroundColor: 'transparent',
                                    overflow: 'auto',
                                    wordBreak: 'break-word',
                                }}
                                showLineNumbers
                                wrapLines={true}
                                lineProps={{
                                    style: {
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    },
                                }}
                            >
                                {producerCode}
                            </SyntaxHighlighter>
                        </CodeBlockContainer>
                    </Box>

                    <Box>
                        <Typography
                            variant="h6"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                        >
                            {translate(
                                'resources.datasets.show.consumerExample'
                            )}
                        </Typography>
                        <CodeBlockContainer>
                            <CopyButton
                                size="small"
                                onClick={() => handleCopy(consumerCode)}
                            >
                                <ContentCopyIcon fontSize="small" />
                            </CopyButton>
                            <SyntaxHighlighter
                                language="python"
                                style={tomorrow}
                                customStyle={{
                                    margin: 0,
                                    padding: '16px',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    backgroundColor: 'transparent',
                                    overflow: 'auto',
                                    wordBreak: 'break-word',
                                }}
                                showLineNumbers
                                wrapLines={true}
                                lineProps={{
                                    style: {
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    },
                                }}
                            >
                                {consumerCode}
                            </SyntaxHighlighter>
                        </CodeBlockContainer>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

const DataSetShow = () => {
    return (
        <Show resource="datasets">
            <DataSetShowLayout />
        </Show>
    );
};

export default DataSetShow;
